import type { Runtime, RuntimeEvent } from '@betool/identity-runtime'

/**
 * La SORTIE MUTUELLE : ce qui fait qu'une déconnexion vaut partout.
 *
 * LE DÉFAUT QUE CE MODULE FERME
 * ---------------------------------------------------------------------------
 * Le trousseau est unique et partagé par toutes les applications du tenant.
 * Se déconnecter dans le widget retire le slot du trousseau — pour de bon, et
 * pour tout le monde. Mais une application qui n'écoute rien garde sa propre
 * session ouverte : l'autre onglet continue d'afficher des écrans pleins et de
 * laisser travailler, alors que l'identité qui les autorisait n'existe plus.
 *
 * C'est exactement ce que la doctrine appelle les gestes mutuels : « retirer
 * ou déconnecter depuis une application vaut partout, il n'y a qu'un
 * trousseau ». Le runtime le DIFFUSE déjà — localement à ses abonnés, et aux
 * autres onglets par son canal. Personne ne l'écoutait.
 *
 * CE QU'ON NE FAIT PAS ICI
 * ---------------------------------------------------------------------------
 * On ne relit rien, on n'adopte rien, on ne décide rien : on ferme. La
 * révocation d'une session locale n'a pas besoin de confirmation — et en
 * demander une ferait dépendre la sortie d'un réseau qui peut être tombé.
 */

/** Ce que l'application doit faire quand son identité s'en va. */
export interface SortieLocale {
  /** Coupe la session SANS appeler le serveur : le jeton est déjà sans valeur. */
  fermer: () => void
}

/**
 * Abonne l'application aux pertes d'identité, et rend la fonction pour se
 * désabonner.
 *
 * DEUX ÉVÉNEMENTS SEULEMENT, et le tri compte :
 *
 *   * `keyring-invalidated` — le trousseau entier ne vaut plus : on sort, quel
 *     que soit le compte de cet onglet ;
 *   * `slot-lost` — un compte a disparu. On ne sort QUE si c'est celui de cet
 *     onglet : sortir sur la perte du compte d'un collègue, dans un autre
 *     onglet, déconnecterait quelqu'un qui n'a rien demandé.
 *
 * `keyring-changed` et `slot-selected` ne sont PAS des motifs de sortie : le
 * premier dit qu'une liste a bougé, le second qu'un autre onglet a choisi.
 */
export function ecouterPerteIdentite(runtime: Runtime, sortie: SortieLocale): () => void {
  // Le slot de CET onglet, suivi au fil de l'eau. On ne peut pas le demander au
  // runtime au moment de la perte : à cet instant, il l'a déjà purgé — et
  // `currentSession()` rendrait `null` pour tout le monde, ce qui ferait sortir
  // les onglets des autres comptes avec.
  let slotDeCetOnglet = runtime.currentSession()?.slotRef ?? null

  return runtime.subscribe((evenement: RuntimeEvent) => {
    switch (evenement.type) {
      case 'slot-selected':
        slotDeCetOnglet = evenement.slotRef
        return

      case 'keyring-invalidated':
        sortie.fermer()
        return

      case 'slot-lost':
        // `expired`, `revoked`, `removed` mènent au même endroit : dans les
        // trois cas l'identité ne porte plus cette session. Les distinguer ne
        // changerait que le message, et il n'y a personne pour le lire — la
        // page va disparaître.
        if (slotDeCetOnglet !== null && evenement.slotRef === slotDeCetOnglet) {
          sortie.fermer()
        }
        return

      default:
        // `keyring-changed` : une liste a bougé, le widget la relira seul.
        return
    }
  })
}
